// src/pages/LoginPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  ExclamationCircleIcon, 
  UserCircleIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
  EnvelopeIcon,
  KeyIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

const LoginPage = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isAdminLogin, setIsAdminLogin] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, error: authError } = useAuth();

  // Paleta de colores actualizada para coincidir con el Navbar
  const colors = {
    primary: '#0C4B45',
    primaryDark: '#083D38',
    primaryLight: '#83F4E9',
    secondary: '#662D8F',
    secondaryLight: '#F2A9FD',
    accent: '#4CAF50',
    accentDark: '#2E7D32',
    backgroundLight: '#F0F9F5',
    backgroundLighter: '#E0F3EB',
    textDark: '#0C4B45',
    textMedium: '#4A6B57',
    textLight: '#B5EAD7',
    error: '#EF5350',
    success: '#4CAF50'
  };

  useEffect(() => {
    setIsAdminLogin(location.pathname.includes('admin'));
  }, [location]);

  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const validateForm = () => {
    const { email, password } = formData;

    if (!email || !password) {
      setError('Email y contraseña son requeridos');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Por favor ingrese un email válido');
      return false;
    }

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const credentials = {
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        isAdmin: isAdminLogin
      };

      const result = await login(credentials);

      if (result.success) {
        setSuccess(true);
        
        setTimeout(() => {
          // El destino se decide por el rol que devolvió el servidor, nunca por
          // el selector visual del formulario. Así un cliente no termina en admin.
          if (result.isAdmin) {
            // ✅ Redirección para administradores
            navigate('/admin/dashboard');
          } else {
            // ✅ Redirección inteligente para usuarios normales
            const from = location.state?.from?.pathname || '/';
            
            // Si viene del carrito o está en una página específica, mantenerla
            if (from.includes('/cart') || from.includes('/checkout')) {
              navigate(from, { replace: true });
            } 
            // Si viene de la página de login o está vacío, redirigir a productos
            else if (from === '/' || from === '/login') {
              navigate('/products');
            } 
            // Para cualquier otra página, mantener la redirección original
            else {
              navigate(from, { replace: true });
            }
          }
        }, 500);
      } else {
        setError(result.error || 'Credenciales incorrectas. Por favor verifique sus datos.');
      }
    } catch (err) {
      console.error('Error al iniciar sesión:', err);
      setError('Ocurrió un error. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="relative mt-20 flex min-h-[calc(100vh-5rem)] items-center justify-center overflow-hidden px-4 py-4"
      style={{ 
        background: `linear-gradient(135deg, ${colors.backgroundLight} 0%, ${colors.backgroundLighter} 100%)`
      }}
    >
      <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-[#83F4E9]/30 blur-3xl" />
      <div className="absolute -bottom-28 -right-20 h-80 w-80 rounded-full bg-[#F2A9FD]/30 blur-3xl" />
      <motion.div 
        className="relative z-10 grid w-full max-w-5xl overflow-hidden rounded-3xl border border-white/80 bg-white/90 shadow-2xl md:grid-cols-[0.9fr_1.1fr]"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        {/* Encabezado */}
        <motion.div 
          className="hidden bg-gradient-to-br from-[#0C4B45] via-[#0b4741] to-[#083D38] px-10 py-8 text-center text-white md:flex md:flex-col md:items-center md:justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <motion.div 
            className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-white/15 shadow-lg ring-1 ring-white/25"
            style={{
              background: isAdminLogin ? `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.secondaryLight} 100%)` : undefined
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {isAdminLogin ? (
              <ShieldCheckIcon className="h-10 w-10 text-white" />
            ) : (
              <UserCircleIcon className="h-10 w-10 text-white" />
            )}
          </motion.div>
          <p className="mb-3 text-xs font-bold tracking-[0.3em] text-[#83F4E9]">PCVASQUEZ</p>
          <h2 className="text-2xl font-bold leading-tight">
            {isAdminLogin ? 'Panel de administración' : 'Tecnología que acompaña tus ideas'}
          </h2>
          <p className="mt-3 max-w-sm text-sm font-light text-white/80">
            {isAdminLogin ? 'Ingresa de forma segura para gestionar tu tienda.' : 'Accede, compra y revisa tus pedidos en un solo lugar.'}
          </p>
        </motion.div>

        {/* Tarjeta de Login */}
        <motion.div 
          className="bg-white"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <div className="p-6 sm:p-7 lg:px-10 lg:py-7">
            <div className="mb-5 md:hidden">
              <p className="text-xs font-bold tracking-[0.25em] text-[#0C4B45]">PCVASQUEZ</p>
              <h2 className="mt-2 text-2xl font-bold text-[#0C4B45]">{isAdminLogin ? 'Acceso administrativo' : 'Bienvenido de vuelta'}</h2>
            </div>
            <div className="mb-5 hidden md:block">
              <h2 className="text-2xl font-bold text-[#0C4B45]">{isAdminLogin ? 'Acceso administrativo' : 'Inicia sesión'}</h2>
              <p className="mt-1 text-sm text-[#4A6B57]">{isAdminLogin ? 'Ingresa tus credenciales autorizadas.' : 'Continúa con tu cuenta para comprar.'}</p>
            </div>
            {error && (
              <motion.div 
                className="mb-8 rounded-xl py-4 px-5 flex items-start"
                style={{ 
                  backgroundColor: `${colors.error}20`, 
                  borderColor: colors.error
                }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <ExclamationCircleIcon 
                  className="h-6 w-6 mr-3 mt-0.5 flex-shrink-0" 
                  style={{ color: colors.error }} 
                />
                <span style={{ color: colors.error }}>{error}</span>
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <motion.div 
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <label htmlFor="email" className="block text-sm font-medium mb-2" style={{ color: colors.textMedium }}>
                  Correo electrónico
                </label>
                <div className="relative rounded-lg shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5" style={{ color: colors.primary }} />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="block w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0C4B45]/30 transition-all duration-200 sm:text-sm"
                    style={{ 
                      color: colors.textDark,
                      borderColor: colors.primaryLight,
                      backgroundColor: `${colors.primaryLight}10`
                    }}
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <label htmlFor="password" className="block text-sm font-medium mb-2" style={{ color: colors.textMedium }}>
                  Contraseña
                </label>
                <div className="relative rounded-lg shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <KeyIcon className="h-5 w-5" style={{ color: colors.primary }} />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    minLength="6"
                    className="block w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0C4B45]/30 transition-all duration-200 sm:text-sm"
                    style={{ 
                      color: colors.textDark,
                      borderColor: colors.primaryLight,
                      backgroundColor: `${colors.primaryLight}10`
                    }}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </div>
              </motion.div>

              {!isAdminLogin && <div className="text-right -mt-2"><Link to="/forgot-password" className="text-sm font-medium hover:underline" style={{ color: colors.primary }}>¿Olvidaste tu contraseña?</Link></div>}

              <motion.button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center py-4 px-6 border border-transparent rounded-xl shadow-lg text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 disabled:opacity-90 disabled:cursor-not-allowed"
                style={{
                  background: isAdminLogin 
                    ? `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.secondaryLight} 100%)`
                    : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`
                }}
                whileHover={!loading ? { scale: 1.02 } : {}}
                whileTap={!loading ? { scale: 0.98 } : {}}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                    Procesando...
                  </>
                ) : (
                  <>
                    {isAdminLogin ? 'Acceder al sistema' : 'Iniciar sesión'}
                    <ArrowRightIcon className="ml-3 h-5 w-5" />
                  </>
                )}
              </motion.button>
            </form>

            {!isAdminLogin && (
              <motion.div className="mt-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t" style={{ borderColor: colors.primaryLight }}></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-3 bg-white" style={{ color: colors.textMedium }}>
                      ¿Nuevo en nuestra plataforma?
                    </span>
                  </div>
                </div>

                <motion.div className="mt-4" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Link
                    to="/register"
                    className="w-full flex justify-center py-3 px-6 rounded-xl shadow-sm text-base font-medium transition-all duration-300 border"
                    style={{ 
                      borderColor: colors.primaryLight,
                      color: colors.secondary,
                      backgroundColor: 'white'
                    }}
                  >
                    Crear una cuenta
                  </Link>
                </motion.div>
              </motion.div>
            )}
          </div>
        </motion.div>

      </motion.div>
    </div>
  );
};

export default LoginPage;
